/**
 * Community Profile GraphQL Fragments
 *
 * Shared field sets reused across profile queries.
 *
 * @fragment CoreUserProfileFields - Basic profile data (bio, location, topics, avatar, topGenres).
 *   Used by: GET_CURRENT_PROFILE, GET_PROFILE_BY_UUID, UPDATE_PROFILE, GET_SETTINGS_PAGE_DATA
 * @fragment UserProfileSocialFields - Social data
 *   (follower counts, viewerContext, inProgress items, system playlists).
 *   Used by: GET_CURRENT_PROFILE, GET_PROFILE_BY_UUID, GET_PUBLIC_PROFILE_BY_UUID
 *   Notes:
 *     - Accepts $contentLanguage for localized inProgress content.
 *     - Requires $includeMemberFields: Boolean! — gates fields whose resolver
 *       requires auth (inProgress, watchHistory). Anonymous viewers MUST pass
 *       false; the fields' [UserMediaPosition!]! type would null-propagate
 *       up to the parent on auth failure otherwise.
 *     - Requires $includeProfileActivity: Boolean! — gates the profile-page-
 *       only activity surface (inProgress, watchHistory, favorites, watchLater).
 *       Surfaces that don't render the activity carousel (library, settings)
 *       MUST pass false to avoid the heavy nested content payload.
 *     - Tile field set is deliberately LEAN — verify against the selection
 *       below before adding to it: it drops `contentAssetReferences` (always
 *       empty in practice), narrows `imageContentRelationships` to the single
 *       `d6-keyart-with-text` type, and drops `contentSeries.featureMedia /
 *       contentAssetReferences / episodesCount / seasonsCount` (unused or
 *       always-null in the tile flow).
 * @fragment UserProfileSummaryFields - Minimal profile data for follower/following lists.
 *   Used by: GET_FOLLOWERS, GET_FOLLOWING
 */

import { gql } from '@apollo/client'

export const CORE_USER_PROFILE_FIELDS = gql`
  fragment CoreUserProfileFields on UserProfile {
    uid
    uuid
    # NOTE: isCircleFacilitator deliberately does NOT live in this shared
    # fragment. It is the newest schema field, and every embedder would
    # hard-fail validation against a gateway that predates it — settings,
    # profile saves, tile cards, for all users. Only the three profile-page
    # queries that render the Sage badge select it (inline, in query.ts).
    bio
    bioVisibility
    location
    birthday
    identityVibe {
      id
      vibe
    }
    profileVisibility
    locationVisibility
    birthdayVisibility
    memberSinceVisibility
    topicsVisibility
    archetypeVisibility
    yourJourneyVisibility
    watchHistoryVisibility
    currentlyWatchingVisibility
    archetype
    yourJourney
    createdAt
    updatedAt
    displayName
    avatar {
      url
      small
      card
    }
    topics(language: $language) {
      contentId
      titleGroupId
      title
    }
    topGenres(limit: 5) {
      name
      cleanName
      weight
      viewCount
    }
  }
`

export const USER_PROFILE_SOCIAL_FIELDS = gql`
  fragment UserProfileSocialFields on UserProfile {
    followerCount
    followingCount
    connectionCount
    viewerContext {
      isOwner
      isFollowing
      isFollowedBy
      visibilityLevel
    }
    # inProgress and watchHistory resolvers require auth and are typed as
    # non-null lists ([UserMediaPosition!]!), so the auth error from an
    # anonymous viewer null-propagates up to userProfilePublic. Gate them
    # with @include so the public query can omit the selection entirely.
    # inProgress is used by both CommunityMyLibraryPage (Currently Watching
    # row) and CommunityProfilePage (in-progress carousel), so it is only
    # gated by $includeMemberFields. watchHistory is profile-page-only
    # (library uses GET_USER_HISTORY for paging) and gets the second gate.
    inProgress(limit: 24) @include(if: $includeMemberFields) {
      mediaId
      uid
      position
      lastPlayed
      watchedCount
      hiddenByUser
      hiddenFromContinue
      media {
        mediaId
        duration
        contentFeatured(language: $contentLanguage, limit: 1) {
          contentId
          contentType
          title
          path(format: RELATIVE)
          shortDescription
          episodeNumber
          seasonNumber
          instructor
          isNew
          displayType
          subType
          studio
          contentAnalytic {
            votesUpCountTotal
            votesCountTotal
            commentsCountTotal
            thumbPopularityMappingId
          }
          featureMedia {
            duration
            mediaId
            contentPolicy {
              accessCheck {
                allowed
              }
            }
          }
          imageContentRelationships(
            types: ["d6-keyart-with-text"]
            language: ["en", "_primary"]
          ) {
            type
            image {
              url
            }
          }
          references(typesByTypeTag: true) {
            contentReferenceType { type }
            contentTo(language: $contentLanguage) {
              title
              path(format: RELATIVE)
              contentType
              contentId
              language
            }
          }
          contentSeries(language: $contentLanguage) {
            title
            path(format: RELATIVE)
            contentId
            contentType
            subType
            displayType
            studio
            shortDescription
            contentAnalytic {
              votesUpCountTotal
              votesCountTotal
              commentsCountTotal
            }
            imageContentRelationships(
              types: ["d6-keyart-with-text"]
              language: ["en", "_primary"]
            ) {
              type
              image {
                url
              }
            }
            references(typesByTypeTag: true) {
              contentReferenceType { type }
              contentTo(language: $contentLanguage) {
                title
                path(format: RELATIVE)
                contentType
                contentId
                language
              }
            }
          }
        }
      }
    }
    # System playlists user-shareable (Watch Later, Favorites). We only need
    # the metadata here — playlistItems are fetched via GET_MY_PLAYLISTS on
    # /playlist/default for the owner. On a non-owner profile view, items are
    # not currently rendered for these system playlists; we display the row
    # link only when visibility is PUBLIC/ANONYMOUS.
    # Gated by $includeProfileActivity: only the profile page surfaces the
    # system-playlist rows here. CommunityMyLibraryPage gets these from
    # GET_MY_PLAYLISTS instead and doesn't need them in the profile fetch.
    favorites @include(if: $includeProfileActivity) {
      id
      playlistName
      description
      visibility
      itemCount
      followerCount
    }
    watchLater @include(if: $includeProfileActivity) {
      id
      playlistName
      description
      visibility
      itemCount
      followerCount
    }
    # Watch History (federated from video-analytics).
    # /playlist/default still uses GET_USER_HISTORY with paging; this fragment
    # provides the first 24 items for the profile-page section render.
    # Gated by $includeWatchHistory which callers compute as the AND of
    # includeMemberFields and includeProfileActivity. Both gates are needed
    # (auth + profile-activity), but GraphQL forbids two @include directives
    # on the same field, so we combine them into a single client-side boolean.
    watchHistory(limit: 24) @include(if: $includeWatchHistory) {
      mediaId
      uid
      position
      lastPlayed
      watchedSum
      hiddenByUser
      media {
        mediaId
        duration
        contentFeatured(language: $contentLanguage, limit: 1) {
          contentId
          contentType
          title
          path(format: RELATIVE)
          shortDescription
          episodeNumber
          seasonNumber
          instructor
          isNew
          displayType
          subType
          studio
          contentAnalytic {
            votesUpCountTotal
            votesCountTotal
            commentsCountTotal
            thumbPopularityMappingId
          }
          featureMedia {
            duration
            mediaId
            contentPolicy {
              accessCheck {
                allowed
              }
            }
          }
          imageContentRelationships(
            types: ["d6-keyart-with-text"]
            language: ["en", "_primary"]
          ) {
            type
            image {
              url
            }
          }
          references(typesByTypeTag: true) {
            contentReferenceType { type }
            contentTo(language: $contentLanguage) {
              title
              path(format: RELATIVE)
              contentType
              contentId
              language
            }
          }
          contentSeries(language: $contentLanguage) {
            title
            path(format: RELATIVE)
            contentId
            contentType
            subType
            displayType
            studio
            shortDescription
            contentAnalytic {
              votesUpCountTotal
              votesCountTotal
              commentsCountTotal
            }
            imageContentRelationships(
              types: ["d6-keyart-with-text"]
              language: ["en", "_primary"]
            ) {
              type
              image {
                url
              }
            }
            references(typesByTypeTag: true) {
              contentReferenceType { type }
              contentTo(language: $contentLanguage) {
                title
                path(format: RELATIVE)
                contentType
                contentId
                language
              }
            }
          }
        }
      }
    }
  }
`

/**
 * @fragment PlaylistItemContentFields - Lean playlist item with nested content details.
 *   Fields: id, contentId, contentType, playlistPosition, and the lean tile
 *   field set on content (title, path, lean thumbnails, references, series,
 *   analytics, featureMedia entitlement/level).
 *   Requires $language: [String] variable in the enclosing operation.
 *   Used by: GET_MY_PLAYLISTS, GET_PLAYLISTS_WITH_ITEMS, GET_PLAYLIST_BY_ID
 *
 *   Lean delta vs the pre-2026-05-11 shape:
 *     - Dropped `contentAssetReferences` (always [] per staging sample;
 *       fallback already served by `imageContentRelationships`).
 *     - Narrowed `imageContentRelationships.types` from 3 types
 *       (d6-keyart-with-text, d6-keyart-no-text, d6-poster-image) to 1
 *       (d6-keyart-with-text — the type getGraphqlHeroImage looks up by
 *       default for TILE_V2_DISPLAY_DEFAULT in Tile.v2/utils.js).
 *     - Dropped `contentSeries.featureMedia` (series have no featureMedia in
 *       real data — always null).
 *     - Dropped `contentSeries.contentAssetReferences` (same as top level).
 *     - Dropped `contentSeries.episodesCount` and `contentSeries.seasonsCount`
 *       (selected but never piped to tile output — buildTileData reads
 *       Oracle-entity fields only).
 *     - Narrowed `contentSeries.imageContentRelationships.types` similarly.
 */
export const PLAYLIST_ITEM_CONTENT_FIELDS = gql`
  fragment PlaylistItemContentFields on PlaylistItem {
    id
    contentId
    contentType
    playlistPosition
    content(language: $language) {
      id
      title
      path(format: RELATIVE)
      contentType
      contentId
      shortDescription
      episodeNumber
      seasonNumber
      instructor
      isNew
      displayType
      subType
      studio
      contentAnalytic {
        votesUpCountTotal
        votesCountTotal
        commentsCountTotal
        thumbPopularityMappingId
      }
      featureMedia {
        duration
        mediaId
        userMediaPosition {
          position
        }
        contentPolicy {
          accessCheck {
            allowed
          }
        }
      }
      imageContentRelationships(
        types: ["d6-keyart-with-text"]
        language: ["en", "_primary"]
      ) {
        type
        image {
          url
        }
      }
      references(types: ["genres"], typesByTypeTag: true) {
        contentReferenceType { type }
        contentTo(language: $language) {
          title
          path(format: RELATIVE)
          contentType
          contentId
          language
        }
      }
      contentSeries(language: $language) {
        title
        path(format: RELATIVE)
        contentId
        contentType
        subType
        displayType
        studio
        shortDescription
        contentAnalytic {
          votesUpCountTotal
          votesCountTotal
          commentsCountTotal
          thumbPopularityMappingId
        }
        imageContentRelationships(
          types: ["d6-keyart-with-text"]
          language: ["en", "_primary"]
        ) {
          type
          image {
            url
          }
        }
        references(types: ["genres"], typesByTypeTag: true) {
          contentReferenceType { type }
          contentTo(language: $language) {
            title
            path(format: RELATIVE)
            contentType
            contentId
            language
          }
        }
      }
    }
  }
`

/**
 * @fragment MemberPlaylistShellFields - SHELL playlist field set for the
 *   member-profile playlist rows (`GET_MEMBER_PLAYLISTS_SHELL`, the
 *   `/profile/<uuid>` playlist section).
 *
 *   Mirrors `MyPlaylistShellFields` (the My-Playlists-tab shell) but adds the
 *   two member-facing follow scalars the profile row needs (`followerCount`,
 *   `isFollowing`). Content is NOT fetched here — each revealed row hydrates
 *   its own tiles per-row via `useRowHydration` → `useTitleGroupHydration` →
 *   `GET_CONTENT_FOR_TILE_HYDRATION` (which also fetches learner level, cast,
 *   audio/subtitle icons etc., giving hover-metadata parity with the My
 *   Playlists tab). This replaced the fat `GET_MEMBER_PLAYLISTS` query whose
 *   inline per-item content fragment cost ~4s per load.
 *
 *   Kept fields and their reader:
 *     - id — row identity / follow-mutation target.
 *     - playlistName / playlistType — row title (system rows re-label to the
 *       localized "Watch Later" / "Favorites" via `getPlaylistDisplayName`) and
 *       the client bucket split (default / favorites / custom).
 *     - visibility — belt-and-suspenders PUBLIC filter (`VISIBILITY_FILTER_SET`).
 *     - description — passed to `PlaylistShareButton`.
 *     - itemCount — the shell "expected count" that drives the header/footer
 *       placeholder + the per-row hydration skeleton width. NOTE: over-counts
 *       unpublished / de-routed rows — user-facing count comes from the
 *       hydrated `items.length`, never this.
 *     - followerCount / isFollowing — the `FollowTag` state on non-owner rows.
 *     - playlistIndex / createdAt / updatedAt — the sort keys behind the
 *       playlist sort control. `playlistIndex` is the member-defined display
 *       order (higher = top) written by the `reorderPlaylists` mutation; the
 *       two dates back the "recently created" / "recently updated" modes.
 *     - playlistItems { id, contentId, titleGroupId, contentType,
 *       playlistPosition } — the id-only item shells `collectShellContentItems`
 *       turns into `titleGroupId`s for hydration.
 */
export const MEMBER_PLAYLIST_SHELL_FIELDS = gql`
  fragment MemberPlaylistShellFields on Playlist {
    id
    playlistName
    playlistType
    visibility
    description
    itemCount
    followerCount
    isFollowing
    playlistIndex
    createdAt
    updatedAt
    playlistItems {
      id
      contentId
      titleGroupId
      contentType
      playlistPosition
    }
  }
`

/**
 * @fragment MediaTileContentFeatured - Tile field set on the Content returned
 *   by `Media.contentFeatured(language: $contentLanguage, limit: 1)`.
 *
 *   Used by `inProgress` / `watchHistory` carousels and lists where each
 *   `UserMediaPosition.media.contentFeatured[0]` resolves the tile data
 *   for the row. Mirrors the lean tile field set used by
 *   `PlaylistItemContentFields` (no `id`, no `userMediaPosition` — those
 *   belong to the PlaylistItem path) so swapping the same source content
 *   between a playlist context and a media-position context keeps the
 *   downstream tile builder code identical.
 *
 *   Requires `$contentLanguage: [String]` in the enclosing operation.
 *
 *   Used by:
 *     - GET_PROFILE_IN_PROGRESS_CAROUSEL
 *     - GET_PROFILE_WATCH_HISTORY_CAROUSEL
 *     - GET_IN_PROGRESS_FULL
 *     - GET_IN_PROGRESS_LIST
 *     - GET_USER_HISTORY
 *
 *   Note: `UserProfileSocialFields.inProgress[*].media.contentFeatured` and
 *   `.watchHistory[*].media.contentFeatured` still inline the same shape.
 *   Composing fragments-inside-fragments would require every consumer of
 *   `UserProfileSocialFields` to also include this fragment, so that
 *   de-duplication is deliberately deferred.
 */
export const MEDIA_TILE_CONTENT_FEATURED = gql`
  fragment MediaTileContentFeatured on Content {
    contentId
    contentType
    title
    path(format: RELATIVE)
    shortDescription
    episodeNumber
    seasonNumber
    instructor
    isNew
    displayType
    subType
    studio
    contentAnalytic {
      votesUpCountTotal
      votesCountTotal
      commentsCountTotal
      thumbPopularityMappingId
    }
    featureMedia {
      duration
      mediaId
      contentPolicy {
        accessCheck {
          allowed
        }
      }
    }
    imageContentRelationships(
      types: ["d6-keyart-with-text", "d6-keyart-no-text"]
      language: ["en", "_primary"]
    ) {
      type
      image {
        url
      }
    }
    references(types: ["genres"], typesByTypeTag: true) {
      contentReferenceType { type }
      contentTo(language: $contentLanguage) {
        title
        path(format: RELATIVE)
        contentType
        contentId
        language
      }
    }
    contentSeries(language: $contentLanguage) {
      title
      path(format: RELATIVE)
      contentId
      contentType
      subType
      displayType
      studio
      shortDescription
      contentAnalytic {
        votesUpCountTotal
        votesCountTotal
        commentsCountTotal
      }
      imageContentRelationships(
        types: ["d6-keyart-with-text"]
        language: ["en", "_primary"]
      ) {
        type
        image {
          url
        }
      }
      references(types: ["genres"], typesByTypeTag: true) {
        contentReferenceType { type }
        contentTo(language: $contentLanguage) {
          title
          path(format: RELATIVE)
          contentType
          contentId
          language
        }
      }
    }
  }
`

/**
 * @fragment OracleContentFields - Content fields shared across Oracle search results.
 *   Used by both top-level content results and playlist-item content in ORACLE_SEARCH_QUERY.
 *   Covers thumbnails, metadata, series info, analytics, and reference tags.
 *   Requires $language: [String] and $learnerLevelLanguage: String! variables in the
 *   enclosing operation.
 *
 *   NOTE: NOT touched by the 2026-05-11 perf trim — Oracle search has its own
 *   resolver path (contentList + relevance), different perf profile, and is
 *   out of scope per source plan §Out of scope.
 */
export const ORACLE_CONTENT_FIELDS = gql`
  fragment OracleContentFields on Content {
    id
    title
    path
    contentType
    contentId
    shortDescription
    episodeNumber
    seasonNumber
    instructor
    isNew
    displayType
    subType
    studio
    primaryLanguage
    cast
    featureMedia {
      mediaId
      duration
      userMediaPosition {
        position
      }
      contentPolicy {
        accessCheck {
          allowed
        }
      }
      mediaAssets {
        mediaReferenceType
        language
        aiMetadata(language: $learnerLevelLanguage) {
          learnerLevel {
            level
          }
        }
      }
    }
    contentAssetReferences(
      contentAssetReferences: [{ type: "keyart-no-text" }]
      limit: 1
    ) {
      asset {
        renderUrl
        width
        height
      }
    }
    imageContentRelationships(
      types: ["d6-keyart-with-text", "d6-keyart-no-text", "d6-poster-image"]
      language: $language
    ) {
      type
      image {
        url
      }
    }
    contentSeries {
      title
      path
      contentId
      studio
    }
    contentAnalytic {
      votesUpCountTotal
      votesCountTotal
      commentsCountTotal
    }
    references(types: [
      "genres", "centers",
      "yoga-style", "yoga-level", "yoga-duration", "yoga-focus",
      "fitness-style", "fitness-level", "fitness-duration", "fitness-focus",
      "meditation-style", "meditation-level", "meditation-duration", "meditation-focus"
    ]) {
      id
      contentReferenceType {
        type
      }
      contentTo(language: $language) {
        title
        contentType
        contentId
        path
        language
      }
    }
  }
`

export const USER_PROFILE_SUMMARY_FIELDS = gql`
  fragment UserProfileSummaryFields on UserProfile {
    uid
    uuid
    bio
    location
    profileVisibility
    displayName
    avatar {
      url
      small
      card
    }
    identityVibe {
      id
      vibe
    }
    viewerContext {
      isOwner
      isFollowing
      isFollowedBy
      visibilityLevel
    }
  }
`
